import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { usePlatform } from '../lib/platform'
import { usePlayer } from '../lib/player'
import { mmss, toTitleCase } from '../lib/format'
import { reachBadge } from '../lib/reach'
// The palette used to live here. It's shared now, so a song is the same
// colour on Home and on the profile as it is in this feed.
import { lyricField } from '../lib/songColour'
import { getCached, setCached } from '../lib/queryCache'
import { Shell } from '../components/Shell'
import { Spinner } from '../components/ui'
import ReportSong from '../components/ReportSong'
import Encourage from '../components/Encourage'
import FollowButton from '../components/FollowButton'

/**
 * The Collective — a full-screen feed of songs, one per screen, playing.
 *
 * This is the discovery mechanism the app exists for: a writer with no church,
 * no following and no way in gets heard because someone kept scrolling. It is
 * built from `app/reels-harness.html`, which already settled the arguments —
 * read that file before changing the shape of this one.
 *
 * Three things carried over from the harness because they are the design, not
 * decoration:
 *
 * 1. **A song with no video is still a reel.** Most songs have no video and
 *    never will. Those play as a lyric card — a colour field with the words
 *    stepping through — rather than being excluded or shown as a black
 *    rectangle. Without this the feed is empty, because almost nothing here
 *    has a clip.
 * 2. **Sixty seconds, enforced on playback, never on the file.** The window is
 *    a start/end pair against the master recording. The full song still has to
 *    reach a church intact.
 * 3. **Desktop is a grid, not a feed.** Scroll-snap autoplay is a touch idiom;
 *    on a mouse it hijacks the wheel and plays audio nobody asked for.
 *
 * The list is entirely RLS-shaped: it asks for every song and gets back only
 * the ones can_reach_song() lets through, so no client-side filter decides who
 * sees what and a bug here can leak nothing.
 */

/** How long the feed gives anything. Sixty seconds, always. */
const CAP = 60


/**
 * How long a line of a lyric should sit on screen.
 *
 * Under about three seconds a line is gone before it has been read, let alone
 * heard — which is what "scrolling way too fast" was: the card divided the
 * whole window by however many lines the song had, so a 24-line lyric got 2.5
 * seconds each regardless of how long any of them takes to sing.
 */
const MIN_DWELL = 3.2

/**
 * Pace the lines across the clip without knowing the song.
 *
 * Two rules, both of them guesses, but far better guesses than dividing evenly:
 *
 * 1. A longer line takes longer to sing, so time is shared by word count
 *    rather than by line count.
 * 2. No line gets less than MIN_DWELL. If the words cannot fit at that pace,
 *    the card shows fewer of them rather than flickering through all of them —
 *    the lyric card is meant to let someone catch the idea, not to display the
 *    whole sheet.
 *
 * Returns the cue time for each line it kept. Real per-line timings, when a
 * writer has synced them, replace this wholesale.
 */
function paceLyrics(lines, span) {
  if (!lines?.length || !(span > 0)) return []
  const fits = Math.max(1, Math.floor(span / MIN_DWELL))
  const kept = lines.slice(0, fits)
  const weight = kept.map((l) => Math.max(1, l.trim().split(/\s+/).length))
  const total = weight.reduce((a, b) => a + b, 0)

  // Share by weight, then pull anything under the floor back up to it. The
  // last line simply runs to the end, so rounding never leaves a gap.
  let at = 0
  const cues = []
  for (let i = 0; i < kept.length; i++) {
    cues.push(at)
    at += Math.max(MIN_DWELL, (weight[i] / total) * span)
  }
  return cues
}

const fullName = (u) => [u?.first_name, u?.last_name].filter(Boolean).join(' ')

export default function Collective() {
  const { user } = useSession()
  const { device } = usePlatform()
  const nav = useNavigate()
  const [rows, setRows] = useState(() => getCached('collective:' + user.id))
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    // A clip is the best version of sixty seconds — shot for this screen,
    // upright, deliberate — so it stays the preferred unit. A cover is a clip
    // owned by someone who isn't the writer, so both kinds sit in the same
    // feed with different credit lines.
    const [clipRes, songRes] = await Promise.all([
      supabase.from('song_clips').select(`
          id, kind, duration_seconds, created_at, owner_id,
          owner:owner_id(first_name, last_name),
          song:song_id(
            id, title, reach, scripture_refs, songwriter_id, duration_seconds,
            hearings_count, writer:songwriter_id(first_name, last_name)
          )
        `).is('removed_at', null).order('created_at', { ascending: false }),
      supabase.from('songs').select(`
          id, title, reach, scripture_refs, songwriter_id, duration_seconds,
          created_at, r2_object_key, lyric_cues, hearings_count,
          writer:songwriter_id(first_name, last_name)
        `).not('r2_object_key', 'is', null).neq('songwriter_id', user.id)
        .order('created_at', { ascending: false }),
    ])
    if (clipRes.error) return setErr(readableError(clipRes.error))
    if (songRes.error) return setErr(readableError(songRes.error))

    // Your own clips aren't news to you; covers of your song are.
    const clips = (clipRes.data ?? [])
      .filter((r) => r.owner_id !== user.id)
      .map((r) => ({
        key: r.id,
        source: { clip_id: r.id },
        media: 'video',
        songId: r.song?.id,
        title: r.song?.title,
        reach: r.song?.reach,
        writer: fullName(r.song?.writer) || 'A writer',
        credit: r.kind === 'cover'
          ? `${fullName(r.owner) || 'A writer'} · covering ${fullName(r.song?.writer) || 'another writer'}`
          : fullName(r.song?.writer) || 'A writer',
        created_at: r.created_at,
        writerId: r.song?.songwriter_id,
        heard: r.song?.hearings_count ?? 0,
        // The whole recording's length, not the clip's -- the mini player is
        // playing the song, and the clip window has nothing to say about it.
        songSeconds: r.song?.duration_seconds ?? null,
        start: 0,
        end: Math.min(r.duration_seconds || CAP, CAP),
      }))

    // A song opened out without a clip is still a song someone let out. It
    // plays as a lyric card over its own recording, capped at sixty seconds.
    const clipped = new Set(clips.map((r) => r.songId))
    const bare = (songRes.data ?? [])
      .filter((s) => !clipped.has(s.id))
      .map((s) => ({
        key: 'song:' + s.id,
        source: { song_id: s.id },
        media: 'audio',
        songId: s.id,
        title: s.title,
        reach: s.reach,
        writer: fullName(s.writer) || 'A writer',
        credit: fullName(s.writer) || 'A writer',
        created_at: s.created_at,
        writerId: s.songwriter_id,
        heard: s.hearings_count ?? 0,
        songSeconds: s.duration_seconds ?? null,
        start: 0,
        end: Math.min(s.duration_seconds || CAP, CAP),
        // Tapped by the writer against this exact recording. A clip is a
        // different file with its own clock, so cues are never carried over to
        // one -- they would be confidently wrong rather than merely estimated.
        cues: s.lyric_cues,
      }))

    /**
     * Least heard first, then newest.
     *
     * This is the fair queue, and it is the whole ordering rule: a song rises
     * because few people have heard it, never because many have. A writer with
     * no following is heard for the same reason a known one is, which is the
     * promise that makes posting here worth doing. Popularity is deliberately
     * not an input -- ranking by it would build the leaderboard this product
     * has spent its whole design avoiding.
     *
     * hearings_count moves once per person per song (record_hearing refuses to
     * count a replay), so replaying your own find cannot bury it for everyone.
     */
    // No hue field any more: the colour is derived from songId at render
    // time by lib/songColour, so there is nothing here to keep in sync.
    const all = [...clips, ...bare]
      .sort((a, b) => (a.heard - b.heard)
        || (new Date(b.created_at) - new Date(a.created_at)))

    // The words for the lyric cards, in one query rather than one per slide.
    const ids = all.filter((r) => r.media === 'audio').map((r) => r.songId)
    if (ids.length) {
      const { data: secs } = await supabase.from('song_sections')
        .select('song_id, lyrics, order_position').in('song_id', ids)
        .order('order_position')
      const byId = {}
      for (const s of secs ?? []) {
        const lines = (s.lyrics || '').split('\n').map((l) => l.trim()).filter(Boolean)
        byId[s.song_id] = (byId[s.song_id] ?? []).concat(lines)
      }
      for (const r of all) {
        const written = byId[r.songId] ?? []
        // Saved cues win, but only while they still describe these words. A
        // lyric edited after it was synced would leave the times pointing at
        // lines that have moved, so a mismatch falls back to pacing rather
        // than holding the wrong line up in front of a listener.
        const saved = r.cues?.lines
        const matches = saved?.length
          && saved.every((c, i) => c.text === written[i])
        if (matches) {
          r.lyrics = saved.map((c) => c.text)
          r.cueTimes = saved.map((c) => c.at)
        } else {
          r.lyrics = written.slice(0, 24)
          r.cueTimes = null
        }
      }
    }

    setCached('collective:' + user.id, all)
    setRows(all)
  }, [user.id])

  useEffect(() => { load() }, [load])

  if (rows === null) return <Shell tabs><Spinner /></Shell>

  const empty = rows.length === 0
  const feed = device !== 'desktop'

  return (
    <Shell tabs>
      {empty
        ? <Empty />
        : feed
          ? <Feed rows={rows} nav={nav} />
          : <Grid rows={rows} nav={nav} />}
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}

function Empty() {
  return (
    <div className="rl-wrap">
      <div className="rl-empty">
        <div>
          <h2>Nothing has reached you yet.</h2>
          <p>
            This fills as other writers open songs out. Your own songs never
            appear here — the feed is for hearing people you haven't met.
            Being in someone's circle is the quickest way in.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Signed playback URLs, resolved lazily and kept.
 *
 * A presign lasts five minutes, which is shorter than a browse. Rather than
 * signing everything up front and watching the tail of the feed go stale, this
 * signs the slide you are on plus the next one, and re-signs on a media error
 * — which is what an expired URL looks like from the element's side.
 */
function useSources() {
  const [src, setSrc] = useState({})
  const pending = useRef(new Set())

  const resolve = useCallback(async (row, force) => {
    if (!row || pending.current.has(row.key)) return
    if (src[row.key] && !force) return
    pending.current.add(row.key)
    const { data } = await supabase.functions
      .invoke('r2-playback-url', { body: row.source })
    pending.current.delete(row.key)
    if (data?.url) setSrc((m) => ({ ...m, [row.key]: data.url }))
  }, [src])

  return [src, resolve]
}

function Feed({ rows, nav }) {
  const [index, setIndex] = useState(0)
  const [muted, setMuted] = useState(true)
  const feedRef = useRef(null)
  const [src, resolve] = useSources()
  const player = usePlayer()
  const runId = useRef(0)

  /**
   * Hand the sixty seconds over to the whole song.
   *
   * The clip is the pitch and the mini player is where a full song lives
   * everywhere else in the app, so "hear the whole song" and "encourage" both
   * end the same way: the hook stops, the recording starts in the bar, and you
   * are still in the feed. Going to another screen to press play again was a
   * step that bought nothing.
   */
  const handOff = useCallback(async (row, { onlyIfIdle = false } = {}) => {
    if (!row?.songId) return
    // Encouraging passes onlyIfIdle. Saying something nice about a song must
    // never be the thing that stops it -- handing off from the sheet button
    // was toggling the bar off when it was already this song, and restarting
    // from zero when it was another one. If anything is loaded in the bar, it
    // keeps playing and the sheet just opens over the top of it.
    if (onlyIfIdle && player.track) return
    if (player.isCurrent(row.songId)) return player.toggle()
    const { data } = await supabase.functions
      .invoke('r2-playback-url', { body: { song_id: row.songId } })
    if (!data?.url) return
    player.play({
      id: row.songId,
      src: data.url,
      title: row.title || 'Untitled',
      writer: row.writer,
      writerId: row.writerId,
      duration: row.songSeconds,
    })
  }, [player])

  // Sign what is about to be needed, never the whole feed.
  useEffect(() => {
    resolve(rows[index])
    resolve(rows[index + 1])
  }, [index, rows, resolve])

  /**
   * The observer's only job is to say which slide you are on.
   *
   * It deliberately does NOT start or stop playback. When it did, playback was
   * a function of geometry, and the first time the geometry was wrong -- a
   * collapsed height made every slide report as on-screen -- the whole feed
   * played at once, over the top of itself. One index, one thing playing, is a
   * rule the layout cannot break.
   */
  useEffect(() => {
    const el = feedRef.current
    if (!el) return
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (en.isIntersecting && en.intersectionRatio > 0.6) {
          setIndex(Number(en.target.dataset.i))
        }
      }
    }, { threshold: [0, 0.6, 1], root: el })
    for (const s of el.children) io.observe(s)
    return () => io.disconnect()
  }, [rows])

  // Exactly one slide plays: the one you are on. Everything else is paused and
  // rewound, so scrolling back to a song starts it rather than resuming it
  // somewhere in the middle.
  useEffect(() => {
    const el = feedRef.current
    if (!el) return
    // Every run of this effect claims the feed. play() is a promise, and a
    // pause() issued while one is still pending is not reliably honoured --
    // that is how two clips ended up audible at once during a fast scroll.
    // Re-checking the token once play() actually resolves closes the race
    // rather than trusting the abort.
    const mine = ++runId.current
    /**
     * While a song is loaded in the bar, the bar owns the audio -- playing or
     * paused. Keying this on `playing` instead meant pressing pause dropped
     * straight through to the code below and started the clip again, so the
     * pause button looked broken while actually working perfectly: something
     * else simply began making noise a frame later. Closing the bar hands the
     * feed back.
     */
    if (player.track) {
      for (const slide of el.children) slide.querySelector('video, audio')?.pause()
      return
    }
    for (const slide of el.children) {
      const m = slide.querySelector('video, audio')
      if (!m) continue
      if (Number(slide.dataset.i) === index) {
        m.muted = muted
        m.play().then(
          () => { if (runId.current !== mine) { m.pause(); m.currentTime = 0 } },
          () => {},
        )
      } else if (!m.paused) {
        m.pause()
        m.currentTime = 0
      }
    }
  }, [index, muted, rows, src, player.track])

  return (
    <div className="rl-wrap">
      <div className="rl-col">
        <div className="rl-feed" ref={feedRef}>
          {rows.map((row, i) => (
            <Slide key={row.key} row={row} i={i} src={src[row.key]}
                   muted={muted} nav={nav} onHandOff={handOff}
                   onStale={() => resolve(row, true)} />
          ))}
        </div>
        <button className="rl-ico rl-mute" onClick={() => setMuted((m) => !m)}
                aria-label={muted ? 'Sound on' : 'Sound off'}>
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
               strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 5 6 9H3v6h3l5 4V5Z" />
            {muted && <path d="m16 9 4 6M20 9l-4 6" />}
          </svg>
        </button>
      </div>
    </div>
  )
}

function Slide({ row, i, src, muted, nav, onHandOff, onStale }) {
  const media = useRef(null)
  // A hearing is recorded once per slide per visit, and only after enough of it
  // has actually played. Scrolling past something is not hearing it, and the
  // fair queue is only fair if the number underneath it means what it says.
  const counted = useRef(false)
  const [pct, setPct] = useState(0)
  const [line, setLine] = useState(0)
  // Video slides can switch to the words; an audio song is already the words.
  const [mode, setMode] = useState(row.media === 'video' ? 'video' : 'lyric')
  // Encouraging happens over the top of the slide, never on another screen.
  const [encouraging, setEncouraging] = useState(false)
  // The sheet is anchored to the bottom, which is exactly where the now-playing
  // bar lives. When a song is playing it lifts to sit above it, so you can see
  // what you are encouraging while you encourage it.
  const bar = usePlayer().track
  const span = Math.max(0.1, (row.end || CAP) - row.start)
  // Cue times, and the lines that actually fit at a readable pace. Both come
  // from the same pass so the card can never step to a line it isn't showing.
  const cues = useMemo(
    // The writer's own timing if they tapped it; an estimate if they didn't.
    () => row.cueTimes ?? paceLyrics(row.lyrics, span),
    [row.cueTimes, row.lyrics, span],
  )
  const lyrics = (row.lyrics ?? []).slice(0, cues.length)

  // Hold the sixty-second window on playback rather than on the file. Looping
  // back to the in-point is what makes a feed feel like a feed: the clip is
  // still going when you come back up to it.
  const onTime = (e) => {
    const m = e.target
    const end = row.end || CAP
    if (m.currentTime >= end || m.currentTime < row.start - 0.1) {
      m.currentTime = row.start
      return
    }
    const span = Math.max(0.1, end - row.start)
    const elapsed = m.currentTime - row.start
    setPct((elapsed / span) * 100)

    // Two thirds of the window, or fifteen seconds, whichever comes first --
    // long enough to be a real listen, short enough that a clip someone stayed
    // with counts even if they move on before the loop.
    if (!counted.current && row.songId && elapsed >= Math.min(15, span * 0.66)) {
      counted.current = true
      supabase.rpc('record_hearing', {
        p_song: row.songId,
        p_seconds: Math.round(elapsed),
        p_completed: elapsed >= span * 0.95,
      // Fire and forget: a listener must never be interrupted because a
      // counter did not save.
      }).then(() => {}, () => {})
    }
    if (cues.length) {
      let i = 0
      while (i + 1 < cues.length && elapsed >= cues[i + 1]) i++
      setLine(i)
    }
  }

  const showLyric = mode === 'lyric' && lyrics.length > 0
  const noWords = row.media === 'audio' && lyrics.length === 0

  return (
    <div className="rl-slide" data-i={i}>
      {row.media === 'video'
        ? <video ref={media} src={src} playsInline loop muted={muted} preload="metadata"
                 onTimeUpdate={onTime} onError={onStale}
                 style={{ opacity: showLyric ? 0 : 1 }} />
        : <audio ref={media} src={src} loop muted={muted} preload="metadata"
                 onTimeUpdate={onTime} onError={onStale} />}

      {(showLyric || row.media === 'audio') && (
        <div className="rl-lyric" style={{
          background: lyricField(row.songId),
        }}>
          {noWords
            // A song with a recording but no lyric sheet still has to look like
            // something. Its title, held on its own colour, is that something.
            ? <div className="rl-ll now">{toTitleCase(row.title) || 'Untitled'}</div>
            : lyrics.slice(line, line + 3).map((l, n) => (
                <div key={line + '-' + n}
                     className={`rl-ll ${n === 0 ? 'now' : n === 1 ? 'next' : 'later'}`}>{l}</div>
              ))}
        </div>
      )}

      <div className="rl-head">
        <span className="rl-bylinerow">
          {row.writerId ? (
            <button className="rl-byline rl-bylink" onClick={() => nav(`/writer/${row.writerId}`)}>
              {row.credit}
            </button>
          ) : (
            <span className="rl-byline">{row.credit}</span>
          )}
          {/* Next to the name, which is the thing being followed. */}
          <FollowButton writerId={row.writerId} />
        </span>
        <span className="rl-title">{toTitleCase(row.title) || 'Untitled'}</span>
      </div>

      {/* Only a video slide has two ways to be looked at. */}
      {row.media === 'video' && lyrics.length > 0 && (
        <div className="rl-modes">
          <button className={`rl-mode${mode === 'video' ? ' on' : ''}`}
                  onClick={() => setMode('video')} aria-label="Watch the performance">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                 strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2.5" y="6" width="13" height="12" rx="2.4" /><path d="m15.5 12 6-3.4v6.8L15.5 12Z" />
            </svg>
          </button>
          <button className={`rl-mode${mode === 'lyric' ? ' on' : ''}`}
                  onClick={() => setMode('lyric')} aria-label="Read the words">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                 strokeWidth="1.9" strokeLinecap="round">
              <rect x="2.5" y="5" width="19" height="14" rx="2.6" /><path d="M6 11h5M6 14.5h9M14 11h4" />
            </svg>
          </button>
        </div>
      )}

      <div className="rl-rail">
        <button className="rl-ico"
                onClick={() => { setEncouraging(true); onHandOff?.(row, { onlyIfIdle: true }) }}
                aria-label="Encourage this writer">
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
               strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21.2l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.8z" />
          </svg>
        </button>
        <ReportSong songId={row.songId} title={row.title} />
      </div>

      <div className="rl-over">
        {/* The lyric in miniature over a performance — redundant once the whole
            screen is the lyric. */}
        {!showLyric && row.media === 'video' && lyrics.length > 0 && (
          <div className="rl-cap">
            <b>{lyrics[line]}</b><span>{lyrics[line + 1] || ''}</span>
          </div>
        )}
        <span className="rl-pip">{reachBadge(row.reach)}</span>
        <button className="rl-cta" onClick={() => onHandOff?.(row)}>
          Hear the whole song
        </button>
      </div>

      <div className="rl-prog"><i style={{ width: pct + '%' }} /></div>

      {/* Deliberately does not pause: you are encouraging what you are hearing,
          and stopping the song to say so is the wrong instrument. */}
      {encouraging && (
        <div className={`rl-encore${bar ? ' has-bar' : ''}`} role="dialog" aria-modal="true"
             aria-label={`Encourage ${row.title || 'this song'}`}
             onClick={(e) => {
               if (e.target.classList.contains('rl-encore')) setEncouraging(false)
             }}>
          <div className="rl-encore-card">
            <div className="rl-grip" aria-hidden="true" />
            <div className="rl-encore-head">
              <b>{toTitleCase(row.title) || 'Untitled'}</b>
              <button className="btn-plain" onClick={() => setEncouraging(false)}>
                Close
              </button>
            </div>
            <p className="small" style={{ margin: '0 0 12px' }}>{row.credit}</p>
            <Encourage song={{ id: row.songId, title: row.title }}
                       onSent={() => setEncouraging(false)} />
          </div>
        </div>
      )}
    </div>
  )
}

function Grid({ rows, nav }) {
  const [open, setOpen] = useState(null)
  const [src, resolve] = useSources()

  useEffect(() => { if (open) resolve(open) }, [open, resolve])

  return (
    <>
      <div className="rl-grid">
        <h1>The collective</h1>
        <p className="rl-lede">
          Songs writers have opened out far enough to reach you. Play one, then
          look at everything else that writer has going.
        </p>
        <div className="rl-tiles">
          {rows.map((row) => (
            <button key={row.key} className="rl-tile" onClick={() => setOpen(row)}>
              <div className="rl-thumb" style={row.media === 'audio' ? {
                background: lyricField(row.songId),
              } : undefined}>
                {row.media === 'audio' && (
                  <div className="rl-thumbwords">
                    {(row.lyrics ?? []).slice(0, 3).join(' / ') || toTitleCase(row.title)}
                  </div>
                )}
                <span className="rl-tpip">{reachBadge(row.reach)}</span>
                <span className="rl-badge">{mmss((row.end || CAP) - row.start)}</span>
              </div>
              <div className="rl-tt">{toTitleCase(row.title) || 'Untitled'}</div>
              <div className="rl-tw">{row.credit}</div>
            </button>
          ))}
        </div>
      </div>

      {open && (
        <div className="rl-modal" onClick={(e) => {
          if (e.target.classList.contains('rl-modal')) setOpen(null)
        }}>
          <div className="rl-phone" style={open.media === 'audio' ? {
            background: lyricField(open.songId),
          } : undefined}>
            <ModalMedia row={open} src={src[open.key]} />
          </div>
          <div className="rl-side">
            <h3>{toTitleCase(open.title) || 'Untitled'}</h3>
            <p className="rl-tw" style={{ color: 'rgba(255,255,255,.7)' }}>{open.credit}</p>
            <button className="rl-cta" style={{ marginTop: 18 }}
                    onClick={() => nav(`/collective/${open.songId}`)}>
              Hear the whole song
            </button>
          </div>
          <button className="rl-close" onClick={() => setOpen(null)} aria-label="Close">×</button>
        </div>
      )}
    </>
  )
}

/** The same sixty-second window as the feed, in the desktop frame. */
function ModalMedia({ row, src }) {
  const onTime = (e) => {
    const end = row.end || CAP
    if (e.target.currentTime >= end) e.target.currentTime = row.start
  }
  if (row.media === 'video') {
    return <video src={src} playsInline autoPlay controls onTimeUpdate={onTime} />
  }
  return (
    <div className="rl-lyric" style={{ position: 'absolute', inset: 0 }}>
      {(row.lyrics ?? []).slice(0, 3).map((l, n) => (
        <div key={n} className={`rl-ll ${n === 0 ? 'now' : n === 1 ? 'next' : 'later'}`}>{l}</div>
      ))}
      <audio src={src} autoPlay controls onTimeUpdate={onTime}
             style={{ position: 'absolute', bottom: 18, width: '86%' }} />
    </div>
  )
}
